'use client'

// https://21st.dev/danielpetho/typewriter/default

import { m, Variants } from 'motion/react'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

interface TypewriterProps {
  text: string | string[]
  speed?: number
  initialDelay?: number
  waitTime?: number
  deleteSpeed?: number
  loop?: boolean
  className?: string
  showCursor?: boolean
  hideCursorOnType?: boolean
  cursorChar?: string | React.ReactNode
  cursorAnimationVariants?: {
    initial: Variants['initial']
    animate: Variants['animate']
  }
  cursorClassName?: string
}

const Typewriter = ({
  text,
  speed = 50,
  initialDelay = 0,
  waitTime = 2000,
  deleteSpeed = 30,
  loop = true,
  className,
  showCursor = true,
  hideCursorOnType = false,
  cursorChar = '|',
  cursorClassName = 'ml-1',
  cursorAnimationVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.01,
        repeat: Infinity,
        repeatDelay: 0.4,
        repeatType: 'reverse',
      },
    },
  },
}: TypewriterProps) => {
  const [displayText, setDisplayText] = useState('')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [currentTextIndex, setCurrentTextIndex] = useState(0)

  const texts = Array.isArray(text) ? text : [text]

  const queueNextFrame = () => {
    const currentText = texts[currentTextIndex] ?? ''

    const startTyping = () => {
      if (isDeleting) {
        if (displayText === '') {
          if (currentTextIndex === texts.length - 1 && !loop) {
            return undefined
          }
          return setTimeout(() => {
            setIsDeleting(false)
            setCurrentTextIndex((prev) => (prev + 1) % texts.length)
            setCurrentIndex(0)
          }, waitTime)
        }
        return setTimeout(() => {
          setDisplayText((prev) => prev.slice(0, -1))
        }, deleteSpeed)
      }

      if (currentIndex < currentText.length) {
        return setTimeout(() => {
          setDisplayText((prev) => prev + currentText[currentIndex])
          setCurrentIndex((prev) => prev + 1)
        }, speed)
      }

      if (texts.length > 1) {
        return setTimeout(() => {
          setIsDeleting(true)
        }, waitTime)
      }

      return undefined
    }

    // Apply initial delay only at the start
    if (currentIndex === 0 && !isDeleting && displayText === '') {
      return setTimeout(startTyping, initialDelay)
    }
    return startTyping()
  }

  useEffect(() => {
    const timeout = queueNextFrame()
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
    // The animation advances by mutating these reactive values; `text` and the
    // timing props are included so the effect re-fires (restarting typing) when
    // a consumer swaps text mid-run — e.g. a non-looping Typewriter whose state
    // ticks have already stopped. queueNextFrame is rebuilt each render and
    // intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    text,
    displayText,
    currentIndex,
    isDeleting,
    currentTextIndex,
    speed,
    deleteSpeed,
    waitTime,
    initialDelay,
    loop,
  ])

  return (
    <div className={`inline tracking-tight whitespace-pre-wrap ${className}`}>
      <span>{displayText}</span>
      {showCursor && (
        <m.span
          variants={cursorAnimationVariants}
          className={cn(
            cursorClassName,
            hideCursorOnType &&
              (currentIndex < (texts[currentTextIndex]?.length ?? 0) ||
                isDeleting)
              ? 'hidden'
              : '',
          )}
          initial="initial"
          animate="animate"
        >
          {cursorChar}
        </m.span>
      )}
    </div>
  )
}

export { Typewriter }
